"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { Activity } from "@/types";
import { activityService } from "@/services/activityService";
import { toast } from "sonner";

interface ActivityContextType {
  activities: Activity[];
  loading: boolean;
  error: string | null;
  addActivity: (activity: Omit<Activity, "id" | "createdAt">) => void;
  fetchActivities: () => Promise<void>;
  refreshActivities: () => Promise<void>;
}

const ActivityContext = createContext<ActivityContextType | undefined>(undefined);

export function ActivityProvider({ children }: { children: ReactNode }) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await activityService.getAllActivities();
      const transformed = data.map((act: any) => ({
        id: act.id,
        userId: act.userId || act.user_id,
        action: act.action,
        module: act.module,
        recordId: act.recordId || act.record_id,
        description: act.description,
        createdAt: act.createdAt || act.created_at,
      }));
      setActivities(transformed);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch activities";
      setError(errorMessage);
      console.error("Error fetching activities:", error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshActivities = useCallback(async () => {
    await fetchActivities();
  }, [fetchActivities]);

  const addActivity = useCallback(
    (activityData: Omit<Activity, "id" | "createdAt">) => {
      setActivities((prev) => {
        const newActivity: Activity = {
          ...activityData,
          id: `ACT${String(prev.length + 1).padStart(3, "0")}`,
          createdAt: new Date().toISOString(),
        };
        return [newActivity, ...prev.slice(0, 49)]; // Keep last 50 activities
      });
    },
    []
  );

  // Fetch activities on mount
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const value: ActivityContextType = {
    activities,
    loading,
    error,
    addActivity,
    fetchActivities,
    refreshActivities,
  };

  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}

export function useActivities() {
  const context = useContext(ActivityContext);
  if (context === undefined) {
    throw new Error("useActivities must be used within an ActivityProvider");
  }
  return context;
}

